/*
 * /auth/reset-password (page)
 */
import { supabase } from "@emr/auth/client"
import {
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Input,
	Label,
	Page,
	toast,
} from "@emr/react-ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Loader2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import z from "zod"

const resetPasswordSearchSchema = z.object({
	token_hash: z.string().optional(),
	type: z.string().optional(),
})

export const Route = createFileRoute("/auth/reset-password")({
	component: ResetPasswordPage,
	validateSearch: resetPasswordSearchSchema,
})

type Status = "loading" | "ready" | "invalid"

function ResetPasswordPage() {
	const navigate = useNavigate()
	const { token_hash, type } = Route.useSearch()
	const [status, setStatus] = useState<Status>("loading")
	const [password, setPassword] = useState("")
	const [confirmPassword, setConfirmPassword] = useState("")
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const resolved = useRef(false)

	useEffect(() => {
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((event, session) => {
			if (resolved.current) return
			if (event === "PASSWORD_RECOVERY" && session) {
				resolved.current = true
				setStatus("ready")
			}
		})

		if (token_hash && type === "recovery") {
			// Admin-generated link: verify the token hash directly in the browser,
			// bypassing Supabase's /auth/v1/verify URL entirely.
			supabase.auth
				.verifyOtp({ token_hash, type: "recovery" })
				.then(({ data, error }) => {
					if (resolved.current) return
					resolved.current = true
					setStatus(error || !data.session ? "invalid" : "ready")
				})
		} else {
			// Standard recovery flow: token arrives via URL hash after Supabase redirect.
			supabase.auth.getSession().then(({ data: { session } }) => {
				if (resolved.current) return
				if (session) {
					resolved.current = true
					setStatus("ready")
				}
			})
			const timeout = setTimeout(() => {
				if (!resolved.current) {
					resolved.current = true
					setStatus("invalid")
				}
			}, 3000)
			return () => {
				subscription.unsubscribe()
				clearTimeout(timeout)
			}
		}

		return () => { subscription.unsubscribe() }
	}, [])

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault()
		setError(null)

		if (password !== confirmPassword) {
			setError("Passwords don't match")
			return
		}
		if (password.length < 8) {
			setError("Password must be at least 8 characters")
			return
		}

		setLoading(true)
		const { error } = await supabase.auth.updateUser({ password })
		setLoading(false)

		if (error) {
			setError(error.message)
		} else {
			toast.success("Password set. You can now log in.")
			await supabase.auth.signOut()
			navigate({ to: "/auth" })
		}
	}

	if (status === "loading") {
		return (
			<Page className={"bg-primary/30"}>
				<Loader2 className={"size-8 animate-spin text-muted-foreground"} />
			</Page>
		)
	}

	if (status === "invalid") {
		return (
			<Page className={"bg-primary/30"}>
				<Card className={"w-full max-w-md p-8"}>
					<CardHeader className={"items-center"}>
						<CardTitle>Link Invalid or Expired</CardTitle>
					</CardHeader>
					<CardContent className={"flex flex-col items-center gap-4"}>
						<p className={"text-center text-sm text-muted-foreground"}>
							This password reset link is invalid or has already been used. Ask your
							administrator to generate a new one.
						</p>
						<Button variant={"outline"} onClick={() => navigate({ to: "/auth" })}>
							Back to Login
						</Button>
					</CardContent>
				</Card>
			</Page>
		)
	}

	return (
		<Page className={"bg-primary/30"}>
			<Card className={"w-full max-w-md p-8"}>
				<CardHeader className={"items-center"}>
					<CardTitle>Set New Password</CardTitle>
				</CardHeader>
				<CardContent>
					<form className={"flex flex-col gap-4"} onSubmit={handleSubmit}>
						<div className={"grid gap-2"}>
							<Label htmlFor={"password"}>New Password</Label>
							<Input
								id={"password"}
								type={"password"}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder={"At least 8 characters"}
								autoComplete={"new-password"}
								required
								minLength={8}
							/>
						</div>
						<div className={"grid gap-2"}>
							<Label htmlFor={"confirm-password"}>Confirm Password</Label>
							<Input
								id={"confirm-password"}
								type={"password"}
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								placeholder={"Repeat password"}
								autoComplete={"new-password"}
								required
							/>
						</div>
						{error && (
							<p className={"text-center text-sm text-destructive"}>{error}</p>
						)}
						<Button type={"submit"} disabled={loading} className={"w-full"}>
							{loading && <Loader2 className={"mr-2 size-4 animate-spin"} />}
							{loading ? "Saving…" : "Set Password"}
						</Button>
					</form>
				</CardContent>
			</Card>
		</Page>
	)
}
